import { BusinessSignupData } from '../components/onboarding/magic-steps/BusinessSignup';

// Map from magic-onboarding fields to existing Supabase schema
export interface BarberProfile {
  name: string;
  email: string;
  phone: string;
  businessName: string;
  businessAddress: string;
  timezone: string;
  businessType: "solo" | "multi";
  bookingUrl: string;
  services: ServiceType[];
  workingHours: {
    monday: WorkingHoursType;
    tuesday: WorkingHoursType;
    wednesday: WorkingHoursType;
    thursday: WorkingHoursType;
    friday: WorkingHoursType;
    saturday: WorkingHoursType;
    sunday: WorkingHoursType;
  };
  stripeConnected: boolean;
  notificationsEnabled: boolean;
  adminCancellationNotificationsEnabled: boolean;
  remindersEnabled: boolean;
  googleCalendarConnected: boolean;
  logo?: string;
  title?: string;
  bio?: string;
  password?: string;
  suiteNumber?: string;
  googleCalendarId?: string;
  welcomeMessage?: string;
  welcomeGif?: string;
  instagram?: string;
  /** Booking flow / link-in-bio theme key chosen before signup (see BOOKING_THEMES). */
  bookingFlowThemeKey?: string;
}

export interface WorkingHoursType {
  start: string;
  end: string;
  enabled: boolean;
}

export interface ServiceType {
  id: string;
  name: string;
  description: string;
  duration: number;
  price: number;
  waitlistEnabled: boolean;
  bufferTime?: number;
}

/**
 * Maps BusinessSignupData from magic-onboarding to BarberProfile for Supabase
 */
export function mapToBarberProfile(
  data: BusinessSignupData & { bookingFlowThemeKey?: string },
): BarberProfile {
  return {
    // Account Setup -> Profile fields
    name: data.fullName,
    email: data.email,
    phone: data.phone,
    password: data.password,
    
    // Business Setup -> Business Profile fields
    businessName: data.businessName,
    businessAddress: data.businessAddress,
    timezone: data.timezone,
    businessType: data.businessType,
    bookingUrl: data.bookingUrl,
    stripeConnected: data.stripeConnected,
    
    // Branding Setup -> Business Profile fields
    title: data.title,
    bio: data.bio,
    logo: data.logoPreview, // Convert File to string URL
    
    // Schedule Setup -> Working Hours
    workingHours: {
      monday: {
        enabled: data.workingHours.monday?.enabled || false,
        start: data.workingHours.monday?.startTime || "09:00",
        end: data.workingHours.monday?.endTime || "17:00"
      },
      tuesday: {
        enabled: data.workingHours.tuesday?.enabled || false,
        start: data.workingHours.tuesday?.startTime || "09:00",
        end: data.workingHours.tuesday?.endTime || "17:00"
      },
      wednesday: {
        enabled: data.workingHours.wednesday?.enabled || false,
        start: data.workingHours.wednesday?.startTime || "09:00",
        end: data.workingHours.wednesday?.endTime || "17:00"
      },
      thursday: {
        enabled: data.workingHours.thursday?.enabled || false,
        start: data.workingHours.thursday?.startTime || "09:00",
        end: data.workingHours.thursday?.endTime || "17:00"
      },
      friday: {
        enabled: data.workingHours.friday?.enabled || false,
        start: data.workingHours.friday?.startTime || "09:00",
        end: data.workingHours.friday?.endTime || "17:00"
      },
      saturday: {
        enabled: data.workingHours.saturday?.enabled || false,
        start: data.workingHours.saturday?.startTime || "10:00",
        end: data.workingHours.saturday?.endTime || "15:00"
      },
      sunday: {
        enabled: data.workingHours.sunday?.enabled || false,
        start: data.workingHours.sunday?.startTime || "10:00",
        end: data.workingHours.sunday?.endTime || "15:00"
      }
    },
    googleCalendarConnected: data.googleCalendarConnected,
    googleCalendarId: data.googleCalendarId,
    
    // Notification Setup -> Business Profile fields
    notificationsEnabled: data.adminNewBookings,
    adminCancellationNotificationsEnabled: data.adminCancellations,
    remindersEnabled: data.clientNotifications,
    
    // Services Setup -> Business Services
    services: data.services.map(service => ({
      id: service.id,
      name: service.name,
      description: service.description,
      duration: service.duration,
      price: service.price,
      waitlistEnabled: service.waitlistEnabled,
      bufferTime: service.bufferTime
    })),
    
    // Default values for missing fields
    suiteNumber: data.businessSuite || "",
    welcomeMessage: "",
    welcomeGif: "",
    instagram: "",
    bookingFlowThemeKey: data.bookingFlowThemeKey
  };
}

/**
 * Maps BarberProfile back to BusinessSignupData for form editing
 */
export function mapFromBarberProfile(profile: BarberProfile): BusinessSignupData {
  return {
    // Account Setup
    fullName: profile.name,
    email: profile.email,
    phone: profile.phone,
    password: profile.password || "",
    ssoProvider: "",
    ssoConnected: false,
    
    // Business Setup
    businessName: profile.businessName,
    businessAddress: profile.businessAddress,
    businessSuite: profile.suiteNumber || '',
    timezone: profile.timezone,
    businessType: profile.businessType,
    bookingUrl: profile.bookingUrl,
    stripeConnected: profile.stripeConnected,
    
    // Branding Setup
    title: profile.title || "",
    bio: profile.bio || "",
    logoFile: null,
    logoPreview: profile.logo || "",
    
    // Schedule Setup
    startDate: new Date().toISOString().split('T')[0],
    workingHours: {
      monday: {
        enabled: profile.workingHours.monday.enabled,
        startTime: profile.workingHours.monday.start,
        endTime: profile.workingHours.monday.end
      },
      tuesday: {
        enabled: profile.workingHours.tuesday.enabled,
        startTime: profile.workingHours.tuesday.start,
        endTime: profile.workingHours.tuesday.end
      },
      wednesday: {
        enabled: profile.workingHours.wednesday.enabled,
        startTime: profile.workingHours.wednesday.start,
        endTime: profile.workingHours.wednesday.end
      },
      thursday: {
        enabled: profile.workingHours.thursday.enabled,
        startTime: profile.workingHours.thursday.start,
        endTime: profile.workingHours.thursday.end
      },
      friday: {
        enabled: profile.workingHours.friday.enabled,
        startTime: profile.workingHours.friday.start,
        endTime: profile.workingHours.friday.end
      },
      saturday: {
        enabled: profile.workingHours.saturday.enabled,
        startTime: profile.workingHours.saturday.start,
        endTime: profile.workingHours.saturday.end
      },
      sunday: {
        enabled: profile.workingHours.sunday.enabled,
        startTime: profile.workingHours.sunday.start,
        endTime: profile.workingHours.sunday.end
      }
    },
    googleCalendarConnected: profile.googleCalendarConnected,
    googleCalendarId: profile.googleCalendarId || "",
    
    // Notification Setup
    clientNotifications: profile.remindersEnabled,
    reminderTiming: "24h",
    reminderMethod: "email",
    adminNewBookings: profile.notificationsEnabled,
    adminCancellations: profile.adminCancellationNotificationsEnabled,
    
    // Payment Setup
    stripeAccountId: "",
    stripeDashboardUrl: "",
    
    // Services Setup
    services: profile.services.map(service => ({
      id: service.id,
      name: service.name,
      description: service.description,
      duration: service.duration,
      price: service.price,
      bufferTime: service.bufferTime || 0,
      waitlistEnabled: service.waitlistEnabled
    })),
    
    // Completion
    completedSteps: []
  };
} 